// Custom date range filter component
import { useTranslation } from "react-i18next";
import React, { useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import { Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { MUIDataTableColumn } from "mui-datatables";

export function DateRangeFilter(props: {
  filterList: string[][],
  onFilterUpdate: (index: number, filterList: any[], filterType?: string) => void,
  index: number
}) {
  const { filterList, onFilterUpdate, index } = props;
  const { t } = useTranslation();
  const currentFilter = useMemo(() => (filterList)[index] || [], [ filterList, index ]) as string[];
  const fromDate = currentFilter[0] ? DateTime.fromISO(currentFilter[0]) : null;
  const toDate = currentFilter[1] ? DateTime.fromISO(currentFilter[1]) : null;

  const handleFromDateChange = useCallback((date: DateTime | null) => {
    const newFilter = [ ...currentFilter ];
    newFilter[0] = date ? date.toISODate() : '';
    onFilterUpdate(index, newFilter);
  }, [ currentFilter, index, onFilterUpdate ]);

  const handleToDateChange = useCallback((date: DateTime | null) => {
    const newFilter = [ ...currentFilter ];
    newFilter[1] = date ? date.toISODate() : '';
    onFilterUpdate(index, newFilter);
  }, [ currentFilter, index, onFilterUpdate ]);

  return (
    <Box sx={{ display: 'flex', gap: 2, p: 2, flexDirection: 'column' }}>
      <DatePicker
        label={t('AuditLog.filter.dateFrom') || "From Date"}
        value={fromDate}
        onChange={handleFromDateChange}
        slotProps={{
          textField: { size: 'small', fullWidth: true }
        }}
      />
      <DatePicker
        label={t('AuditLog.filter.dateTo') || "To Date"}
        value={toDate}
        onChange={handleToDateChange}
        slotProps={{
          textField: { size: 'small', fullWidth: true }
        }}
      />
    </Box>
  );
}

export function renderDateRangeFilter(dateRangeFilter: string[]) {
  if (dateRangeFilter[0] && dateRangeFilter[1]) {
    return `${dateRangeFilter[0]} - ${dateRangeFilter[1]}`;
  } else if (dateRangeFilter[0]) {
    return `From: ${dateRangeFilter[0]}`;
  } else if (dateRangeFilter[1]) {
    return `To: ${dateRangeFilter[1]}`;
  }
  return '';
}

export function updateDateRangeFilter(filterList: string[][], filterPos: number, index: number) {
  filterList[index].splice(filterPos, 1);
  return filterList;
}

export function displayDateRangeFilter(
  filterList: string[][],
  onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: any
) {
  return (
    <DateRangeFilter
      filterList={filterList}
      onFilterUpdate={(idx, list) => onChange(list, idx, column)}
      index={index}
    />
  );
}

// Custom parameter transformation for date range filtering
export function handleCreatedAtParamsChange(params: string) {
  // Transform the date filter parameters for the API
  const urlParams = new URLSearchParams(params);
  const createdAtFilter = urlParams.getAll('createdAt');

  if (createdAtFilter) {
    // Remove the createdAt param as it's not expected by the API
    urlParams.delete('createdAt');

    try {
      // Parse the date range from the filter
      if (createdAtFilter[0] && createdAtFilter[0].trim()) {
        urlParams.set('from', createdAtFilter[0].trim());
      }
      if (createdAtFilter[1] && createdAtFilter[1].trim()) {
        urlParams.set('to', createdAtFilter[1].trim());
      }
    } catch (error) {
      console.warn('Error parsing createdAt filter:', error);
    }
  }

  return urlParams.toString();
}
